import { DonationForm as DonationFormProps, DonationFrequency } from '@web/graphql-components'
import { create, useStore } from 'zustand'
import { createContext, useContext, useRef } from 'react'
import { AddressInput } from '@web/graphql-types'
import { EMAIL_REGEX } from '../utils'

export enum DonationFormStep {
  UpsellStep = -3,
  ContactUsStep = -2,
  ErrorStep = -1,
  Step1 = 0,
  Step2,
  Step3,
  Step4,
}

type DonationError = {
  [key in string]: string | { [key in string]: string } | null
}

type DonationFormActions = {
  nextStep: () => void
  prevStep: () => void
  setStep: (step: DonationFormStep) => void
  setFormValues: (values: Partial<FormValues>) => void
  updateSettings: (settings: DonationFormProps) => void
  updateErrors: (error: DonationError) => void
  validateForm: (shouldSetErrors?: boolean, ignoreValues?: { [key in string]: true }) => boolean
  setDonationFormRef: (ref: React.RefObject<HTMLDivElement | null>) => void
  scrollIntoView: () => void
}

export type FormValues = {
  amount: number | null
  isDonorPays?: boolean
  givingFrequency?: DonationFrequency
  address?: AddressInput | null
  isPayByInstallments?: boolean
  extraFields?: { [key in string]: string }
}

type DonationFormState = {
  settings: DonationFormProps
  step: DonationFormStep
  prevStep: DonationFormStep
  actions: DonationFormActions
  googleMapsApiKey?: string
  donationFormRef?: React.RefObject<HTMLDivElement | null>
  formValues: FormValues
  errors: DonationError
}

const createDonationFormStore = (state: Partial<DonationFormState>) =>
  create<DonationFormState>((set, get) => ({
    settings: state.settings!,
    step: DonationFormStep.Step1,
    prevStep: DonationFormStep.Step1,
    formValues: state.formValues || {
      amount:
        state.settings!.dollarHandlesDefault === -1 ||
        state.settings!.dollarHandlesDefault === undefined ||
        state.settings!.dollarHandlesDefault === null
          ? null
          : state.settings!.dollarHandles[state.settings!.dollarHandlesDefault]?.amount,
      givingFrequency:
        state.settings!.donationFrequencyDefault === undefined
          ? state.settings!.donationFrequency[0]
          : (state.settings!.donationFrequency[state.settings!.donationFrequencyDefault] ??
            state.settings!.donationFrequency[0]),
      address: null,
      extraFields: {
        firstName: '',
        lastName: '',
        email: '',
        organisation: '',
      },
    },
    googleMapsApiKey: state.googleMapsApiKey,
    errors: {},
    actions: {
      setDonationFormRef: (ref: React.RefObject<HTMLDivElement | null>) => set(() => ({ donationFormRef: ref })),
      scrollIntoView: () => {
        const { settings } = get()
        const el = document.querySelectorAll(`#donation-form-element.u-${settings.id}`)

        if (el.length > 0) {
          const firstVisibleForm = Object.values(el).find((el) => {
            const rect = el.getBoundingClientRect()

            return rect.top !== 0 && rect.bottom !== 0
          })

          if (firstVisibleForm) {
            const { top, bottom } = firstVisibleForm.getBoundingClientRect()
            if (!(top === 0 && bottom === 0)) {
              const headerHeight = document.getElementById('ge-header')?.offsetHeight || 0
              window.scrollTo({
                top: top + window.scrollY - headerHeight - 32,
                behavior: 'smooth',
              })
            }
          }
        }
      },
      nextStep: () => {
        get().actions.scrollIntoView()

        return set((state) => {
          let nextStep = state.step + 1
          const amount = state.formValues.amount
          const upsellOptions = state.settings.styles?.options?.upsellRegularGivingOptions
          const isEligibleForUpsell =
            upsellOptions &&
            amount &&
            amount >= (upsellOptions.minUpsellAmount || 10) &&
            amount <= (upsellOptions.maxUpsellAmount || 200)

          switch (state.step) {
            case DonationFormStep.Step1:
              if (state.formValues.givingFrequency === DonationFrequency.OneTime && isEligibleForUpsell) {
                nextStep = DonationFormStep.UpsellStep
              }
              break
            case DonationFormStep.UpsellStep:
              nextStep = DonationFormStep.Step2
              break
            default:
              break
          }

          return { step: nextStep, prevStep: state.step }
        })
      },
      prevStep: () =>
        set((state) => {
          let prevStep = state.step - 1
          const upsellOptions = state.settings.styles?.options?.upsellRegularGivingOptions
          const isEligibleForUpsell =
            state.formValues.givingFrequency === DonationFrequency.OneTime &&
            upsellOptions &&
            state.formValues.amount &&
            state.formValues.amount >= Number(upsellOptions.minUpsellAmount || 10) &&
            state.formValues.amount <= Number(upsellOptions.maxUpsellAmount || 200)

          switch (state.step) {
            case DonationFormStep.Step2:
              if (isEligibleForUpsell) {
                prevStep = DonationFormStep.UpsellStep
              }
              break
            case DonationFormStep.UpsellStep:
              prevStep = DonationFormStep.Step1
              break
            default:
              break
          }

          return { step: prevStep, prevStep: state.step }
        }),
      setStep: (step: DonationFormStep) => set((state) => ({ step, prevStep: state.step })),
      setFormValues: (values: Partial<FormValues>) =>
        set(({ formValues }) => ({
          formValues: { ...formValues, ...values, extraFields: { ...formValues.extraFields, ...values.extraFields } },
        })),
      updateSettings: (settings: DonationFormProps) => set(() => ({ settings })),
      updateErrors: (errors: DonationError) =>
        set((state) => {
          const prevErrors = state.errors

          if (prevErrors && Object.values(errors)[0] === null) {
            delete prevErrors[Object.keys(errors)[0]]

            return { errors: { ...prevErrors } }
          }

          return { errors: { ...prevErrors, ...errors } }
        }),
      validateForm: (shouldSetErrors?: boolean, ignoreValues?: { [key in string]: true }) => {
        const { formValues, settings } = get()

        const errors: DonationError = {}
        if (!formValues.amount) {
          errors['amount'] = 'Please enter an amount'
        }

        if (settings.minimumAmount && formValues.amount && formValues.amount < settings.minimumAmount) {
          errors['amount'] = `Amount must be at least $${settings.minimumAmount}`
        }

        if (!formValues.extraFields?.email && !ignoreValues?.['email']) {
          errors['email'] = 'Please enter an email'
        }

        if (
          formValues.extraFields?.email &&
          !ignoreValues?.['email'] &&
          !EMAIL_REGEX.test(formValues.extraFields.email)
        ) {
          errors['email'] = 'Please enter a valid email'
        }

        if (shouldSetErrors) {
          set({ errors })
        }

        return Object.keys(errors).length === 0
      },
    },
  }))

type DonationFormStore = ReturnType<typeof createDonationFormStore>

export const DonationFormContext = createContext<DonationFormStore | null>(null)

type DonationFormProviderProps = React.PropsWithChildren<Partial<DonationFormState>>

export const DonationFormProvider = ({ children, ...props }: DonationFormProviderProps) => {
  const storeRef = useRef<DonationFormStore | null>(null)

  if (!storeRef.current) {
    storeRef.current = createDonationFormStore(props)
  }

  return <DonationFormContext.Provider value={storeRef.current}>{children}</DonationFormContext.Provider>
}

export function useDonationFormContext<T>(
  selector: (state: DonationFormState) => T,
  equalityFn?: (left: T, right: T) => boolean,
): T {
  const store = useContext(DonationFormContext)

  if (!store) {
    throw new Error('No context set with useDonationFormContext')
  }

  return useStore(store, selector, equalityFn)
}

export function useDonationFormStore() {
  const store = useContext(DonationFormContext)

  if (!store) {
    throw new Error('No context set with getDonationFormStore')
  }

  return store
}
